import { useState, useRef, useEffect } from "react";
import { Package, Zap, Shield, Clock, RefreshCw, Sparkles, CheckCircle2, Fuel } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface Pack {
  id: string;
  name: string;
  videos: number;
  posters: number;
  price: number;
}

interface AddOn {
  id: string;
  name: string;
  price: number;
}

interface OrderSummaryProps {
  selectedPack: Pack | null;
  selectedAddOns: string[];
  addOns: AddOn[];
  total: number;
  deliveryPreference?: string;
  isSticky?: boolean;
  completedSections?: number;
  totalSections?: number;
}

// Animated price component with count-up effect
const AnimatedPrice = ({ value, className }: { value: number; className?: string }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);
  const { formatPrice } = useLanguage();

  useEffect(() => {
    if (value !== prevValueRef.current) {
      setIsAnimating(true);
      const startValue = prevValueRef.current;
      const endValue = value;
      const duration = 400;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(startValue + (endValue - startValue) * eased);
        setDisplayValue(current);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          prevValueRef.current = value;
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [value]);

  return (
    <span className={cn(
      className,
      "transition-all duration-200",
      isAnimating && "text-neon-cyan scale-105"
    )}>
      {formatPrice(displayValue)}
    </span>
  );
};

// Progress ring component
const ProgressRing = ({ progress, size = 40 }: { progress: number; size?: number }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="hsl(var(--muted) / 0.3)"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="url(#progress-gradient)"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-500 ease-out"
      />
      <defs>
        <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--neon-purple))" />
          <stop offset="100%" stopColor="hsl(var(--neon-cyan))" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const OrderSummary = ({
  selectedPack,
  selectedAddOns,
  addOns,
  total,
  deliveryPreference,
  isSticky = false,
  completedSections = 0,
  totalSections = 7
}: OrderSummaryProps) => {
  const { formatPrice } = useLanguage();
  const selectedAddOnDetails = addOns.filter(addon => selectedAddOns.includes(addon.id));
  const addOnsTotal = selectedAddOnDetails.reduce((sum, addon) => sum + addon.price, 0);

  // Calculate savings (compared to buying individually)
  const savings = selectedPack ? Math.round(selectedPack.price * 0.15) : 0;
  const progress = (completedSections / totalSections) * 100;
  const isComplete = completedSections === totalSections;

  return (
    <div className={cn(
      "rounded-2xl p-5 md:p-6 space-y-5",
      "bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl",
      "border border-neon-purple/20",
      "shadow-[0_0_30px_hsl(var(--neon-purple)/0.1)]",
      "transition-all duration-300",
      isSticky && "md:sticky md:top-24"
    )}>
      {/* Header with progress ring */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            "bg-gradient-to-br from-neon-purple/20 to-neon-pink/20",
            "border border-neon-purple/30"
          )}>
            <Fuel className="w-5 h-5 text-neon-purple" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Order Summary</h3>
            <p className="text-xs text-muted-foreground">Your content fuel</p>
          </div>
        </div>

        {/* Progress ring */}
        <div className="relative">
          <ProgressRing progress={progress} size={44} />
          <span className={cn(
            "absolute inset-0 flex items-center justify-center text-xs font-bold",
            isComplete ? "text-neon-green" : "text-muted-foreground"
          )}>
            {completedSections}/{totalSections}
          </span>
        </div>
      </div>

      {/* Pack details */}
      {selectedPack ? (
        <div className={cn(
          "p-4 rounded-xl",
          "bg-gradient-to-br from-neon-purple/10 to-neon-pink/5",
          "border border-neon-purple/20"
        )}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-neon-cyan" />
              <span className="font-semibold">{selectedPack.name}</span>
            </div>
            <AnimatedPrice value={selectedPack.price} className="font-bold text-neon-cyan" />
          </div>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-neon-cyan" />
              {selectedPack.videos} Videos
            </span>
            <span className="flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-neon-pink" />
              {selectedPack.posters} Posters
            </span>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl border border-dashed border-border/50 text-center">
          <p className="text-sm text-muted-foreground">Select a pack to get started</p>
        </div>
      )}

      {/* Express delivery */}
      {deliveryPreference === "express" && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 animate-fade-in">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-medium">Express 24h Delivery</span>
          </div>
          <span className="text-sm font-bold text-orange-400">+{formatPrice(49)}</span>
        </div>
      )}

      {/* Add-ons */}
      {selectedAddOnDetails.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Add-ons</p>
          {selectedAddOnDetails.map((addon, index) => (
            <div 
              key={addon.id}
              className="flex items-center justify-between py-2 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-neon-green" />
                <span className="text-sm">{addon.name}</span>
              </div>
              <span className="text-sm font-medium">+{formatPrice(addon.price)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Total */}
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold">Total</span>
        <AnimatedPrice 
          value={total} 
          className="text-2xl font-extrabold bg-gradient-to-r from-neon-cyan to-neon-purple bg-clip-text text-transparent" 
        />
      </div>

      {/* Savings badge */}
      {savings > 0 && selectedPack && (
        <div className={cn(
          "flex items-center justify-center gap-2 py-2 px-3 rounded-lg",
          "bg-neon-green/10 border border-neon-green/30",
          "animate-fade-in"
        )}>
          <Sparkles className="w-4 h-4 text-neon-green" />
          <span className="text-sm font-medium text-neon-green">
            You save {formatPrice(savings)} vs single videos!
          </span>
        </div>
      )}

      {/* Trust badges */}
      <div className="grid grid-cols-3 gap-2 pt-2">
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-muted/20">
          <Shield className="w-4 h-4 text-neon-green" />
          <span className="text-[10px] text-muted-foreground text-center">Secure Payment</span>
        </div>
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-muted/20">
          <Clock className="w-4 h-4 text-neon-cyan" />
          <span className="text-[10px] text-muted-foreground text-center">72h Delivery</span>
        </div>
        <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-muted/20">
          <RefreshCw className="w-4 h-4 text-neon-pink" />
          <span className="text-[10px] text-muted-foreground text-center">97% Reorder</span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
